import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import Quill from 'quill';
import { of } from 'rxjs';

// Register mention module globally for Quill
import 'quill-mention';
import TableUp, {
  defaultCustomSelect,
  TableVirtualScrollbar,
  TableAlign,
  TableResizeScale,
  TableSelection,
  TableMenuContextmenu,
  TableResizeLine,
} from 'quill-table-up';
import { quillSharedToolbar } from 'src/app/config/quill-shared-toolbar';

// Side-effect import to ensure table-up and blot formatter are registered once
import '../quill-table-up-setup';


@Component({
  selector: 'app-adc-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  templateUrl: './adc-editor.component.html',
  styleUrl: './adc-editor.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AdcEditorComponent,
      multi: true,
    },
  ],
})
export class AdcEditorComponent {
  @Input() config: any;
  @Input() readOnly: boolean = false;
  @Input() placeholder: string = '';
  @Input() content: string = '';
  @Output() editorReady = new EventEmitter<any>();
  @Output() onEditorCreate = new EventEmitter<any>();
  @Output() customAction = new EventEmitter<{
    action: string;
    editor: any;
    payload?: any;
  }>();
  @Output() dropped = new EventEmitter<any>();

  // Mirror ngx-quill outputs so existing templates can bind without changes
  @Output() onSelectionChanged = new EventEmitter<any>();
  @Output() onFocus = new EventEmitter<any>();
  @Output() onBlur = new EventEmitter<any>();
  @Output() onContentChanged = new EventEmitter<any>();

  quillModules: any;
  private quillRef?: Quill;
  private hasUserTyped = false;



  mentionList: any[] = [];
  showDropdown = false;
  activeEditor: any;
  mentionIndex: number = 0;
  dropdownPosition = { top: 0, left: 0 };

  ngOnInit() {
    this.buildModules();
  }

  ngOnChanges(changes: any) {
    if (changes && 'content' in changes) {
      const next = changes.content.currentValue;
      if (this.quillRef) {
        try {
          const cur = (this.quillRef as any).root?.innerHTML || '';
          const isCurEmpty = this.isEmptyHtml(cur);
          if (!this.hasUserTyped && (isCurEmpty || (typeof next === 'string' && next !== cur))) {
            this.replaceContent(this.quillRef, next || '');
          }
        } catch {}
      }
    }
  }

  // Backward compatible handler name expected by templates
  // onEditorCreated(quill: Quill) {
  //   this.handleEditorCreated(quill);
  // }

  onEditorCreated(quill: Quill) {
    try { console.log('[adc-editor] onEditorCreated fired'); } catch {}
    try {
      const hasMention = !!(quill as any).getModule && !!(quill as any).getModule('mention');
      console.log('[adc-editor] modules', {
        hasToolbar: !!(quill as any).getModule && !!(quill as any).getModule('toolbar'),
        hasMention,
        modulesConfigured: Object.keys((this.quillModules || {})).join(',')
      });
    } catch {}
    try {
      if (this.config?.sharedToolbar?.enabled) {
        // Register editor with shared toolbar manager
        quillSharedToolbar.registerEditor(quill, this.config?.sharedToolbar?.selectorId);
        // Tag container to allow CSS to hide any inline toolbar if present
        try {
          const q: any = quill as any;
          const containerEl: HTMLElement | null = (q.container as HTMLElement) || (q.root?.parentElement as HTMLElement) || null;
          if (containerEl) {
            containerEl.classList.add('uses-shared-toolbar');
          }
        } catch {}
      }
    } catch {}
    // Insert initial content if provided without forcing focus/selection
    try {
      const cur = (quill as any).root?.innerHTML || '';
      const isCurEmpty = this.isEmptyHtml(cur);
      if (!this.isEmptyHtml(this.content) && isCurEmpty) {
        this.replaceContent(quill, this.content);
      }
    } catch {}
    this.quillRef = quill;
    this.editorReady.emit(quill);
    this.customAction.emit({ action: 'created', editor: quill });
    const toolbarModule = quill?.getModule('toolbar') as any;
    if (toolbarModule && this.config?.modules?.toolbar?.handlers) {
      Object.keys(this.config.modules.toolbar.handlers).forEach((key) => {
        toolbarModule.addHandler(key, (value?: any) => {
          this.customAction.emit({
            action: key,
            editor: quill,
            payload: value,
          });
        });
      });
    }

    // register icons if provided in config.icons
    const iconsConfig = this.config?.modules?.icons || {};
    const quillIcons = Quill.import('ui/icons') as any;
    Object.keys(iconsConfig).forEach((key) => {
      const item = iconsConfig[key];
      if (typeof item === 'string') {
        quillIcons[key] = item;
      } else if (item.icon) {
        quillIcons[key] = item.icon;
      }
    });

    // Wire Quill core events to outputs so host components can react
    try {
      const root = (quill as any).root as HTMLElement;
      if (root) {
        // Detect '@' presses at the editor level to aid debugging
        try {
          root.addEventListener('keyup', (e: KeyboardEvent) => {
            const k: any = (e as any).key || '';
            const code: any = (e as any).keyCode || (e as any).which;
            if (k === '@' || (code === 50 && e.shiftKey)) {
              console.log('[adc-editor] keyup @ detected', { key: k, code, shift: e.shiftKey });
            }
          });
        } catch {}
        root.addEventListener('focusin', (ev) => this.onFocus.emit({ event: ev, editor: quill }));
        root.addEventListener('focusout', (ev) => this.onBlur.emit({ event: ev, editor: quill }));
      }
      (quill as any).on('selection-change', (range: any, oldRange: any, source: any) => {
        this.onSelectionChanged.emit({ range, oldRange, source, editor: quill });
        if (range == null) {
          this.onBlur.emit({ event: { type: 'selection-blur' }, editor: quill });
        }
      });
      (quill as any).on('text-change', (delta: any, oldDelta: any, source: any) => {
        this.hasUserTyped = true;
        this.onContentChanged.emit({ delta, oldDelta, source, editor: quill });
      });
    } catch {}

    // Clipboard matchers: convert legacy spans into custom embeds so attributes/classes persist
    try {
      const Delta = Quill.import('delta') as any;
      (quill as any).clipboard.addMatcher('SPAN', (node: HTMLElement, delta: any) => {
        try {
          const cls = node.className || '';
          const classes = cls.split(/\s+/);
          
          // Presented by label
          if (classes.includes('presentedByCls')) {
            return new Delta().insert({ ownerlabel: {} });
          }
          
          // Legacy atwho mention - preserve all attributes
          if (classes.includes('atwho-inserted')) {
            const userId = node.getAttribute('fkuserid') || node.getAttribute('id') || '';
            const name = (node.textContent || '').trim().replace(/[\uFEFF\u200B]/g, ''); // Strip zero-width chars
            const isexternal = node.getAttribute('isexternal') || '';
            return new Delta().insert({ atwhomention: { userId, name, isexternal } });
          }
          
          // Agenda duration chip
          if (classes.includes('agDurCls')) {
            // parse minutes between the labels, strip zero-width chars
            const html = (node.innerHTML || '').replace(/[\uFEFF\u200B]/g, '');
            const m = /agDurLabelCls\'\>\[ <\/span>(.*?)<span class=\'agDurLabelCls\'> Minutes \]<\/span>/i.exec(html.replace(/"/g, "'"));
            const minutes = m && m[1] ? m[1].trim() : '0';
            return new Delta().insert({ duration: { minutes } });
          }
          
          // Due date chip from tasks
          if (classes.includes('minDateCls')) {
            const code = node.getAttribute('code') || '';
            // strip label span and zero-width chars
            const text = (node.innerHTML || '').replace(/<span[^>]*class=['"]dueLabelCls['"][^>]*>[^<]*<\/span>/i, '').replace(/[\uFEFF\u200B]/g, '').trim();
            return new Delta().insert({ date: { code, text } });
          }
        } catch {}
        return delta;
      });
    } catch {}

    // Enable pointer events on duration chips so clicks can trigger popover
    try {
      const root = (quill as any).root as HTMLElement;
      if (root) {
        root.addEventListener('click', (ev: MouseEvent) => {
          const target = ev.target as HTMLElement;
          // If clicking inside agDurCls or agDurLabelCls, ensure event bubbles
          if (target && (target.classList.contains('agDurCls') || target.classList.contains('agDurLabelCls'))) {
            // Event will naturally bubble to parent handlers
          }
        }, true);
      }
    } catch {}
  }

  private buildModules() {
    const userModules = this.config?.modules || {};
    const userToolbar = (userModules.toolbar?.container || userModules.toolbar || []) as any[];
    const userToolbarHandlers = userModules.toolbar?.handlers || {};

    const wantsTable = this.config?.enableTable === true;

    const toolbar = Array.isArray(userToolbar) ? [...userToolbar] : userToolbar;

    if (wantsTable && Array.isArray(toolbar)) {
      toolbar.push([{ 'table-up': [] }]); // add button only if required
    }

    this.quillModules = {
      ...userModules,
      // Always create a normal per-instance toolbar; the shared-toolbar manager will takeover
      // We hide the inline toolbar via CSS to prevent blinking, but buttons remain available
      toolbar: { container: Array.isArray(toolbar) ? toolbar : userToolbar, handlers: userToolbarHandlers },
      blotFormatter2: {
        align: {
          allowAligning: true,
        },
        resize: {
          allowResizing: true,
        },
        delete: {
          allowKeyboardDelete: true,
        },
        image: {
          allowAltTitleEdit: true,
          allowCompressor: true,
          linkOptions: {
            allowLinkEdit: true,
          },
        },
      },
      ...(wantsTable
        ? {
            'table-up': {
              customSelect: defaultCustomSelect,
              customBtn: true,
              modules: [
                { module: TableVirtualScrollbar },
                { module: TableAlign },
                { module: TableResizeLine },
                { module: TableResizeScale, options: { blockSize: 12 } },
                { module: TableSelection, options: { selectColor: '#00ff8b4d' } },
                { module: TableMenuContextmenu, options: { localstorageKey: 'used-color', tipText: true } },
              ],
            },
          }
        : {}),
    };
  }

  // For debugging if needed
  onSelectionChanges(event: any) {
    // console.log(event, 'selection event');
  }

  // insertContent(quillEditor: any, html: any) {
  //   try {
  //     if (!quillEditor || html == null) return;
  //     const htmlStr = this.coerceHtml(html);
  //     if (!htmlStr) return;
  //     quillEditor.clipboard.dangerouslyPasteHTML(0, htmlStr, 'silent');
  //   } catch {}
  // }

  private replaceContent(quillEditor: any, html: any) {
    try {
      const htmlStr = this.coerceHtml(html);
      const cur = quillEditor?.root?.innerHTML || '';
      if (htmlStr == null || htmlStr === cur) return;
      quillEditor.setContents([]);
      quillEditor.clipboard.dangerouslyPasteHTML(0, htmlStr, 'silent');
    } catch {}
  }

  private coerceHtml(html: any): string {
    try {
      if (html == null) return '';
      if (typeof html === 'string') return html;
      const maybeSafe = (html as any) as { changingThisBreaksApplicationSecurity?: string };
      if (maybeSafe && typeof maybeSafe.changingThisBreaksApplicationSecurity === 'string') {
        return maybeSafe.changingThisBreaksApplicationSecurity as string;
      }
      if (typeof (html as any).toString === 'function') {
        return String(html);
      }
    } catch {}
    return '';
  }

  private isEmptyHtml(html: string): boolean {
    if (!html) return true;
    const cleaned = html.replace(/\s+/g, '').toLowerCase();
    return cleaned === '' || cleaned === '<p></p>' || cleaned === '<p><br></p>';
  }
}
